import { useQuery } from "@apollo/client";
import Bar from "../states/Bar";
import DetailBlock from "./DetailBlock";
import { formatBuyerRef, formatMatchSummary } from "./buyerNeedDisplay";
import { GET_LISTING_BUYER_MATCHES_FOR_COMPANY } from "../../graphql/buyerNeedsQueries";

/**
 * Which of the office's buyers this listing suits — the reason a broker brings a
 * listing to the meeting at all.
 *
 * Buyers appear as the last four characters of their id and nothing else. The
 * agent beside them is who to talk to. That is the whole point of showing this
 * on a screen that may be shared outside the office.
 */
const BuyerMatches = ({ listingId }) => {
  const { data, loading, error } = useQuery(
    GET_LISTING_BUYER_MATCHES_FOR_COMPANY,
    { variables: { listingId }, fetchPolicy: "cache-and-network", skip: !listingId }
  );

  const matches = data?.getCompanyListingBuyerMatches ?? [];

  // A broker who cannot see office-wide matches should see no block at all
  // rather than an error where the buyers would be.
  if (error) return null;

  if (loading && !matches.length) {
    return (
      <DetailBlock title="Buyer Matches">
        {[0, 1].map((i) => (
          <div key={i} className="flex items-center gap-3 py-1.5">
            <Bar w={28} h={28} r={14} />
            <Bar w={104} h={12} />
            <Bar w="38%" h={11} />
          </div>
        ))}
      </DetailBlock>
    );
  }

  if (!matches.length) {
    return (
      <DetailBlock title="Buyer Matches">
        <p className="text-sm text-gray-400">
          No buyers in this office are looking for a property like this yet.
        </p>
      </DetailBlock>
    );
  }

  return (
    <DetailBlock
      title="Buyer Matches"
      badge={`${matches.length} ${matches.length === 1 ? "buyer" : "buyers"} in this office`}
    >
      {matches.map((match) => (
        <div
          key={match._id}
          className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 py-2 last:border-b-0"
        >
          <span className="h-7 w-7 shrink-0 rounded-full bg-light_green" />
          {/* Labelled the same way the Buyer Needs tab labels it — on its own
              the code means nothing to someone reading a shared screen. */}
          <span className="whitespace-nowrap text-sm text-dark_gray">
            <b className="font-extrabold">Buyer ID</b>{" "}
            <span className="font-bold text-blue">{formatBuyerRef(match) ?? "—"}</span>
          </span>
          <span className="text-sm text-secondary">{formatMatchSummary(match)}</span>
          {match.agentName && (
            <span className="ml-auto text-xs text-gray-400">{match.agentName}</span>
          )}
        </div>
      ))}
    </DetailBlock>
  );
};

export default BuyerMatches;
